import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Home, Sofa, Leaf, Package, Building2, HardHat, Archive, ShoppingBag, Globe, Lock, Zap, ArrowRight, CheckCircle } from 'lucide-react';
import { CTAStrip } from '../components/CTAStrip';
type Category = 'all' | 'domestic' | 'commercial' | 'specialist';
const services = [{
  category: 'domestic' as Category,
  icon: Home,
  title: 'House Clearance',
  desc: 'Full or partial property clearances handled with care, speed, and eco-responsibility.',
  benefits: ['Same-day available', 'Fully insured team', '90%+ recycling rate'],
  href: '/services/house-clearance',
  from: '£150'
}, {
  category: 'domestic' as Category,
  icon: Trash2,
  title: 'House Moves',
  desc: 'Local and long-distance residential removals with professional packing options.',
  benefits: ['Careful handling', 'Packing service available', 'Flexible scheduling'],
  href: '/services/house-clearance',
  from: '£200'
}, {
  category: 'domestic' as Category,
  icon: Sofa,
  title: 'Furniture Removal',
  desc: 'Single items to full room clearances — sofas, beds, wardrobes and more.',
  benefits: ['No job too small', 'Responsible disposal', 'Quick turnaround'],
  href: '/services/house-clearance',
  from: '£60'
}, {
  category: 'domestic' as Category,
  icon: Leaf,
  title: 'Garden Waste',
  desc: 'Green waste, soil, branches, and full garden clearances.',
  benefits: ['Composting where possible', 'All green waste accepted', 'Licensed disposal'],
  href: '/services/garden-waste',
  from: '£80'
}, {
  category: 'domestic' as Category,
  icon: Package,
  title: 'Single Item Pick-Up',
  desc: 'Sofas, fridges, mattresses, appliances — we collect single bulky items.',
  benefits: ['Fixed price', 'Same-day options', 'Eco-disposal'],
  href: '/services/house-clearance',
  from: '£40'
}, {
  category: 'commercial' as Category,
  icon: Building2,
  title: 'Office Clearances',
  desc: 'Complete office strip-outs and furniture removal with minimal disruption.',
  benefits: ['Out-of-hours available', 'Data security options', 'Full documentation'],
  href: '/services/office-removals',
  from: '£200'
}, {
  category: 'commercial' as Category,
  icon: HardHat,
  title: "Builder's Waste",
  desc: 'Construction debris, rubble, plasterboard, and site clearance.',
  benefits: ['Skip alternative', 'Licensed carrier', 'Rapid turnaround'],
  href: '/services/commercial-waste',
  from: '£120'
}, {
  category: 'commercial' as Category,
  icon: Archive,
  title: 'Commercial Waste Bins',
  desc: 'Regular scheduled bin collections for businesses of all sizes.',
  benefits: ['Flexible schedules', 'Compliance documentation', 'Competitive rates'],
  href: '/services/commercial-waste',
  from: '£80/collection'
}, {
  category: 'commercial' as Category,
  icon: ShoppingBag,
  title: 'Shopfitter Waste',
  desc: 'Retail fit-out waste, fixtures, and fittings removal.',
  benefits: ['Fast clearance', 'Weekend available', 'Fully insured'],
  href: '/services/commercial-waste',
  from: '£150'
}, {
  category: 'commercial' as Category,
  icon: Trash2,
  title: 'Commercial Waste Collection',
  desc: 'General business waste management and collection services.',
  benefits: ['Contract options', 'Volume discounts', 'Eco reporting'],
  href: '/services/commercial-waste',
  from: '£80'
}, {
  category: 'specialist' as Category,
  icon: Globe,
  title: 'Overseas / Migration Moves',
  desc: 'International removals with professional packing crates and shipping coordination.',
  benefits: ['Packing crates supplied', 'Customs documentation', 'Door-to-door service'],
  href: '/services/overseas-removals',
  from: '£500'
}, {
  category: 'specialist' as Category,
  icon: Lock,
  title: 'Confidential Waste Disposal',
  desc: 'Secure document and data destruction with full certification.',
  benefits: ['GDPR compliant', 'Certificate of destruction', 'Secure chain of custody'],
  href: '/services/commercial-waste',
  from: '£100'
}, {
  category: 'specialist' as Category,
  icon: Zap,
  title: 'Rapid Response',
  desc: 'Emergency same-day waste collection when you need it most.',
  benefits: ['2-hour response', 'Available 7 days', 'No extra charge'],
  href: '/contact',
  from: '£100'
}];
const tabs: {
  key: Category;
  label: string;
}[] = [{
  key: 'all',
  label: 'All Services'
}, {
  key: 'domestic',
  label: 'Domestic'
}, {
  key: 'commercial',
  label: 'Commercial'
}, {
  key: 'specialist',
  label: 'Specialist'
}];
export function ServicesPage() {
  const [activeTab, setActiveTab] = useState<Category>('all');
  const filtered = activeTab === 'all' ? services : services.filter(s => s.category === activeTab);
  return <div className="pt-16 lg:pt-20" data-id="element-1063">
      {/* Hero */}
      <section className="py-16 px-4" style={{
      background: 'linear-gradient(135deg, #1B8F4B 0%, #0F5D2E 100%)'
    }} data-id="element-1064">
        <div className="max-w-7xl mx-auto text-center" data-id="element-1065">
          <p className="text-green-300 text-sm font-bold uppercase tracking-widest mb-3" data-id="element-1066">
            What We Offer
          </p>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-1067">
            Our Services
          </h1>
          <p className="text-green-100 text-lg max-w-2xl mx-auto" data-id="element-1068">
            From single-item collections to full commercial contracts —
            eco-friendly, fully insured, and always on time.
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="sticky top-16 lg:top-20 z-30 bg-white border-b border-gray-200 shadow-sm" data-id="element-1069">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1070">
          <div className="flex gap-1 overflow-x-auto py-3" data-id="element-1071">
            {tabs.map(({
            key,
            label
          }) => <button key={key} onClick={() => setActiveTab(key)} className="px-5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200" style={{
            backgroundColor: activeTab === key ? '#1B8F4B' : 'transparent',
            color: activeTab === key ? '#fff' : '#6B7280'
          }} data-id="element-1072">
                {label}
              </button>)}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-1073">
        <div className="max-w-7xl mx-auto" data-id="element-1074">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1075">
            {filtered.map(({
            icon: Icon,
            title,
            desc,
            benefits,
            href,
            from
          }) => <div key={title} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col" data-id="element-1076">
                  <div className="p-6 flex-1" data-id="element-1077">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#F0FDF4'
              }} data-id="element-1078">
                      <Icon className="w-6 h-6" style={{
                  color: '#1B8F4B'
                }} data-id="element-1079" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-1080">
                      {title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4" data-id="element-1081">{desc}</p>
                    <ul className="space-y-1.5 mb-4" data-id="element-1082">
                      {benefits.map(b => <li key={b} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1083">
                          <CheckCircle className="w-4 h-4 flex-shrink-0" style={{
                    color: '#1B8F4B'
                  }} data-id="element-1084" />
                          {b}
                        </li>)}
                    </ul>
                    <p className="text-sm font-bold" style={{
                color: '#1B8F4B'
              }} data-id="element-1085">
                      From {from}
                    </p>
                  </div>
                  <div className="px-6 pb-6 flex gap-3" data-id="element-1086">
                    <Link to="/contact" className="flex-1 text-center py-2.5 rounded-lg text-sm font-bold text-white transition-colors hover:opacity-90" style={{
                backgroundColor: '#1B8F4B'
              }} data-id="element-1087">
                      Get Quote
                    </Link>
                    <Link to={href} className="flex-1 text-center py-2.5 rounded-lg text-sm font-bold border transition-colors hover:bg-gray-50" style={{
                borderColor: '#1B8F4B',
                color: '#1B8F4B'
              }} data-id="element-1088">
                      Learn More
                    </Link>
                  </div>
                </div>)}
          </div>
        </div>
      </section>

      <CTAStrip data-id="element-1089" />
    </div>;
}